/**
 * Landing — Lodestar royal cosmic luxury landing page (Phase V).
 *
 * "A royal observatory for your life."
 *
 * Palette:
 *   • Royal Purple #12051F / #2A0F45 / #4B1E72 / #6D35A8
 *   • Celestial Gold #D4AF37 / #F1D78A / #B98A2E / #FFE9A6
 *   • Sacred White #FFFFFF / #F8F5FF / #EDE6FA / #CFC4E8
 *
 * Typography
 *   • Display: Bodoni Moda (italic shimmer for the cinematic line)
 *   • Section: Playfair Display
 *   • Body: Inter
 */
import { Suspense, useEffect, useRef, useState } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { Link } from "react-router-dom";
import {
    ArrowRight, Sparkles,
    Heart, Calendar, ShieldCheck, Sun, Clock,
} from "lucide-react";

import LodestarMark from "@/components/LodestarMark";
import CosmicBackdrop from "@/components/CosmicBackdrop";
import Footer from "@/components/Footer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";
import {
    IconAskUniverse, IconCheckMatch, IconTodayPrediction,
} from "@/components/CelestialIcons";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

// Phase W (perf): below-the-fold sections are loaded only when the user
// scrolls near them. Webpack code-splits this into a separate chunk so
// the Landing's first-paint payload stays small.
const LandingBelowFold = lazy(() => import("@/components/landing/LandingBelowFold"));

// Proof strip — public aggregate signal from /api/proof/summary.
// Self-hides when data is sparse, so safe to mount unconditionally.
// Lazy because it's not critical to first paint.
const ProofStrip = lazy(() => import("@/components/landing/ProofStrip"));


// ────────────────────────────────────────────────────────────────────────────
// NAV
// ────────────────────────────────────────────────────────────────────────────
function NavBar({ user }) {
    return (
        <nav
            data-testid="landing-nav"
            className="relative z-20 max-w-screen-2xl mx-auto px-6 lg:px-12 pt-6 lg:pt-8 flex items-center justify-between"
        >
            <Link to="/" className="flex items-center gap-3" data-testid={TID.nav.logo}>
                <LodestarMark size={44} />
                <div className="leading-tight">
                    <div className="font-display text-[22px] tracking-[0.04em] text-lodestar-white-pure">Lodestar</div>
                    <div className="text-[9px] font-sans tracking-[0.36em] uppercase text-lodestar-gold-champagne/85">
                        royal cosmic guidance
                    </div>
                </div>
            </Link>
            <div className="hidden md:flex items-center gap-9 text-[12.5px] font-sans text-lodestar-white-mist">
                <Link to="/today"       className="hover:text-lodestar-gold-soft transition-colors" data-testid="nav-today">Today&rsquo;s sky</Link>
                <a href="#hooks"        className="hover:text-lodestar-gold-soft transition-colors">Features</a>
                <a href="#overview"     className="hover:text-lodestar-gold-soft transition-colors">Life Overview</a>
                <a href="#how"          className="hover:text-lodestar-gold-soft transition-colors">How it works</a>
                <Link to="/pricing"     className="hover:text-lodestar-gold-soft transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-3">
                <LanguageSwitcher variant="dark" testId="landing-lang-switcher" />
                {user ? (
                    <Link to="/account" data-testid={TID.nav.account}
                          className="royal-cta-link">
                        My account
                    </Link>
                ) : (
                    <Link to="/login" data-testid={TID.nav.signIn}
                          className="hidden sm:inline-block royal-cta-link">
                        Sign in
                    </Link>
                )}
                <Link
                    to="/start"
                    data-testid={TID.nav.getStarted}
                    className="royal-cta-secondary text-[12.5px] px-5 py-2.5"
                >
                    Start reading
                </Link>
            </div>
        </nav>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// HERO
// ────────────────────────────────────────────────────────────────────────────
function Hero() {
    const { t } = useTranslation();
    return (
        <section className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-36 lg:pb-48">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                {/* Copy column */}
                <div className="lg:col-span-7 text-center lg:text-left">
                    <div className="royal-kicker">
                        <Sparkles className="w-3 h-3" /> {t("landing.kicker")}
                    </div>

                    <h1 className="royal-display text-[3rem] sm:text-6xl lg:text-[5.6rem] xl:text-[6.4rem] leading-[0.94] mt-8">
                        {t("landing.hero_line1")}<br />
                        <em>{t("landing.hero_line2")}</em>
                    </h1>

                    <p className="royal-body text-[15.5px] sm:text-base lg:text-[17px] mt-8 max-w-xl mx-auto lg:mx-0 leading-[1.72]">
                        {t("landing.subhead")}
                    </p>

                    <p className="mt-5 text-[11.5px] font-sans uppercase tracking-[0.28em] text-lodestar-white-mist/55">
                        {t("landing.tagline")}
                    </p>

                    <div className="mt-11 flex flex-wrap gap-4 items-center justify-center lg:justify-start">
                        <Link
                            to="/start"
                            data-testid={TID.landing.heroCta}
                            className="royal-cta-primary group"
                        >
                            <Sparkles className="w-3.5 h-3.5" />
                            {t("landing.cta_primary")}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                        <a
                            href="#hooks"
                            data-testid={TID.landing.heroSecondary}
                            className="royal-cta-secondary"
                        >
                            {t("landing.cta_secondary")}
                        </a>
                    </div>

                    <div className="mt-16 grid grid-cols-3 gap-x-8 gap-y-6 max-w-xl mx-auto lg:mx-0">
                        {[
                            [t("landing.trio_one_question"),   t("landing.trio_one_sub")],
                            [t("landing.trio_two_charts"),     t("landing.trio_two_sub")],
                            [t("landing.trio_every_today"),    t("landing.trio_every_sub")],
                        ].map(([n, l]) => (
                            <div key={n} className="text-center lg:text-left">
                                <div className="font-display text-2xl lg:text-3xl text-lodestar-white-pure leading-none">{n}</div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-lodestar-white-mist/60 mt-2 font-sans leading-tight">{l}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Emblem column */}
                <div className="lg:col-span-5 flex flex-col items-center" data-testid="landing-hero-emblem">
                    <div className="relative">
                        {/* Soft golden halo */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-lodestar-gold-royal/30 to-lodestar-plum/25 blur-3xl scale-110" />
                        {/* Slow orbital ring */}
                        <svg viewBox="0 0 100 100"
                             className="absolute inset-0 w-full h-full"
                             style={{ animation: "slowOrbit 220s linear infinite", transformOrigin: "50% 50%" }}>
                            <ellipse cx="50" cy="50" rx="46" ry="46"
                                     fill="none" stroke="rgba(212,175,55,0.30)" strokeWidth="0.18"
                                     strokeDasharray="0.4 1.5" />
                        </svg>
                        <div className="relative">
                            <LodestarMark size={360} />
                        </div>
                        {/* Twinkles around the mark */}
                        <span className="royal-sparkle" style={{ top: "12%", left: "8%",  animationDelay: "0.0s" }} />
                        <span className="royal-sparkle" style={{ top: "30%", right: "5%", animationDelay: "0.8s" }} />
                        <span className="royal-sparkle" style={{ bottom: "15%", left: "20%", animationDelay: "1.6s" }} />
                        <span className="royal-sparkle" style={{ bottom: "8%", right: "12%", animationDelay: "2.2s" }} />
                    </div>
                    <div className="mt-7 text-center">
                        <div className="font-display text-2xl text-lodestar-white-pure tracking-[0.22em]">LODESTAR</div>
                        <div className="text-[10px] font-sans tracking-[0.32em] uppercase text-lodestar-gold-champagne mt-2">
                            your guide to the universe
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// 3 LUXURY HOOK CARDS
// ────────────────────────────────────────────────────────────────────────────
const HOOKS = [
    {
        key: "ask",
        Icon: IconAskUniverse,
        title: "Ask the Universe",
        subtitle: "One question. One moment. One cosmic answer.",
        description:
            "Ask about love, career, timing, money, travel, or decisions and receive focused guidance based on the exact moment you ask.",
        advancedLabel: "Powered by KP Horary / Prashna",
        cta: "Ask My Question",
        to: "/horary",
        accent: "#A78BFA",
    },
    {
        key: "match",
        Icon: IconCheckMatch,
        title: "Check Your Match",
        subtitle: "See the energy between two people.",
        description:
            "Enter both birth profiles to understand attraction, emotional fit, communication, compatibility, timing, strengths, and friction points.",
        advancedLabel: "Two birth profiles · one verdict",
        cta: "Check Our Match",
        to: "/synastry",
        accent: "#F472B6",
    },
    {
        key: "today",
        Icon: IconTodayPrediction,
        title: "Today's Prediction",
        subtitle: "Your personal sky for today.",
        description:
            "Get today's guidance for mood, love, study, career, money, timing, lucky color, lucky number, and one practical action.",
        advancedLabel: "A daily sky written for your chart",
        cta: "View Today's Prediction",
        to: "/start",
        accent: "#FFE9A6",
    },
];


function Hooks() {
    return (
        <section id="hooks" className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
            <div className="text-center mb-16">
                <div className="royal-kicker mx-auto">Three ways to begin</div>
                <h2 className="royal-display text-4xl lg:text-6xl mt-6 leading-[1.06]">
                    Pick your moment.<br />
                    <em>Lodestar does the rest.</em>
                </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
                {HOOKS.map((h) => (
                    <article
                        key={h.key}
                        className="royal-hook-card group"
                        data-testid={`landing-hook-${h.key}`}
                        style={{ "--hook-accent": h.accent }}
                    >
                        {/* Icon block */}
                        <div className="flex items-start justify-between">
                            <div className="w-16 h-16 rounded-2xl grid place-items-center border shrink-0"
                                 style={{
                                     background: `linear-gradient(140deg, ${h.accent}22 0%, rgba(75,30,114,0.45) 100%)`,
                                     borderColor: `${h.accent}55`,
                                 }}>
                                <h.Icon size={40} />
                            </div>
                            <Sparkles className="w-3 h-3 text-lodestar-gold-soft/60 group-hover:text-lodestar-gold-soft transition-colors" />
                        </div>

                        <h3 className="font-heading text-[26px] lg:text-[28px] text-lodestar-white-pure mt-7 leading-snug">
                            {h.title}
                        </h3>
                        <p className="font-display italic text-[15px] text-lodestar-gold-soft mt-2 leading-snug">
                            {h.subtitle}
                        </p>

                        <p className="text-[13.5px] text-lodestar-white-mist mt-5 leading-[1.65] flex-1">
                            {h.description}
                        </p>

                        <div
                            className="mt-5 text-[10px] font-sans uppercase tracking-[0.22em]"
                            style={{ color: h.accent }}
                            data-testid={`landing-hook-${h.key}-advanced`}
                        >
                            {h.advancedLabel}
                        </div>

                        <Link
                            to={h.to}
                            data-testid={`landing-hook-${h.key}-cta`}
                            className="royal-cta-secondary mt-6 self-start group/cta"
                        >
                            {h.cta}
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cta:translate-x-1" />
                        </Link>
                    </article>
                ))}
            </div>
        </section>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// LIFE OVERVIEW PREVIEW — Daily Sky + Upcoming + Character + Strengths
// ────────────────────────────────────────────────────────────────────────────
function LifeOverviewPreview() {
    return (
        <section
            id="overview"
            data-testid="landing-life-overview"
            className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-24 lg:py-32"
        >
            <div className="text-center mb-16">
                <div className="royal-kicker mx-auto">Your Life Overview · preview</div>
                <h2 className="royal-display text-4xl lg:text-6xl mt-6 leading-[1.06]">
                    The first page<br />
                    <em>made for you.</em>
                </h2>
                <p className="royal-body max-w-2xl mx-auto mt-7 text-[14.5px]">
                    Today&rsquo;s sky, the next twelve moons, your natural pattern, and the
                    strengths to lean into. Deeper analysis is ready when you want it.
                </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-6">

                {/* Daily Sky preview */}
                <div className="lg:col-span-8 royal-panel" data-testid="preview-daily-sky">
                    <div className="flex items-start justify-between gap-4 mb-5">
                        <div>
                            <div className="royal-kicker">
                                <Sun className="w-3 h-3" /> Daily Sky
                            </div>
                            <h3 className="font-heading text-2xl text-lodestar-white-pure mt-3">
                                Today, the sky for you
                            </h3>
                        </div>
                        <span className="royal-badge royal-badge-verified">
                            <ShieldCheck className="w-3 h-3" />Code-verified
                        </span>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                            ["Mood",            "Quietly ambitious",      "#F1D78A"],
                            ["Love energy",     "Tender, attentive",      "#F472B6"],
                            ["Career / study",  "Sharp, decisive",        "#A78BFA"],
                            ["Best time window","6:30 — 9:00 pm",         "#FFE9A6"],
                            ["Lucky color",     "Antique gold",           "#D4AF37"],
                            ["One practical action", "Send the message you've been drafting.", "#34D399"],
                        ].map(([k, v, c]) => (
                            <div key={k}
                                 className="rounded-xl p-3.5 bg-lodestar-royal/40 border border-lodestar-imperial/50">
                                <div className="text-[9.5px] font-sans uppercase tracking-[0.22em] text-lodestar-white-mist/65">{k}</div>
                                <div className="text-[13.5px] mt-1.5 text-lodestar-white-pearl leading-snug"
                                     style={{ borderLeft: `2px solid ${c}`, paddingLeft: "0.5rem" }}>
                                    {v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Character & Temperament */}
                <div className="lg:col-span-4 royal-panel" data-testid="preview-character">
                    <div className="royal-kicker">
                        <Heart className="w-3 h-3" /> Character & Temperament
                    </div>
                    <h3 className="font-heading text-2xl text-lodestar-white-pure mt-3">
                        Your natural pattern
                    </h3>
                    <div className="flex flex-wrap gap-2 mt-5">
                        {[
                            "Calm strategist",
                            "Deep thinker",
                            "Relationship-sensitive",
                            "Ambitious builder",
                            "Emotionally intuitive",
                        ].map((t) => (
                            <span key={t} className="royal-badge text-[10.5px]">{t}</span>
                        ))}
                    </div>
                    <p className="royal-body text-[12.5px] mt-5 leading-[1.65]">
                        Lodestar describes the patterns you arrived with — warm, specific,
                        never reductive.
                    </p>
                </div>

                {/* Upcoming Opportunities & Challenges — 12-month strip */}
                <div className="lg:col-span-8 royal-panel" data-testid="preview-upcoming">
                    <div className="flex items-baseline justify-between gap-4 mb-4">
                        <div>
                            <div className="royal-kicker">
                                <Calendar className="w-3 h-3" /> Upcoming Opportunities & Challenges
                            </div>
                            <h3 className="font-heading text-2xl text-lodestar-white-pure mt-3">
                                Next twelve moons, plotted along your dasha lifeline
                            </h3>
                        </div>
                    </div>
                    <div className="relative">
                        {/* gold timeline line */}
                        <div className="absolute left-0 right-0 top-7 h-px bg-gradient-to-r from-transparent via-lodestar-gold-royal/60 to-transparent" />
                        <div className="grid grid-cols-12 gap-1.5 relative">
                            {Array.from({ length: 12 }).map((_, i) => {
                                const tone = [3, 7].includes(i) ? "watchout"
                                           : [1, 5, 9].includes(i) ? "bright"
                                           : "steady";
                                const c = tone === "watchout" ? "#FCA5A5"
                                        : tone === "bright"   ? "#34D399"
                                        : "#F1D78A";
                                const monthKey = new Date(2026, i, 1).toLocaleString("en", { month: "short" });
                                return (
                                    <div key={`${monthKey}-${tone}`} className="flex flex-col items-center pt-2">
                                        <div className="w-2.5 h-2.5 rounded-full"
                                             style={{
                                                 background: c,
                                                 boxShadow: `0 0 8px ${c}88, 0 0 1px ${c}`,
                                             }} />
                                        <div className="text-[9px] font-sans uppercase tracking-[0.14em] text-lodestar-white-mist/60 mt-2">
                                            {monthKey}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-5 text-[10.5px] font-sans uppercase tracking-[0.18em]">
                        <span className="text-[#34D399]">● Opportunity</span>
                        <span className="text-[#FCA5A5]">● Watchout</span>
                        <span className="text-lodestar-gold-soft">● Steady</span>
                    </div>
                </div>

                {/* Strengths & Watchouts split card */}
                <div className="lg:col-span-4 grid grid-rows-2 gap-4" data-testid="preview-strengths-watchouts">
                    <div className="royal-panel">
                        <div className="royal-kicker">
                            <Sparkles className="w-3 h-3" /> Strengths
                        </div>
                        <ul className="mt-4 space-y-2 text-[12.5px] text-lodestar-white-pearl leading-snug">
                            <li>· You read rooms before anyone speaks</li>
                            <li>· You finish what you start</li>
                            <li>· You earn trust by staying</li>
                        </ul>
                    </div>
                    <div className="royal-panel">
                        <div className="royal-kicker" style={{ color: "#FCA5A5" }}>
                            <Clock className="w-3 h-3" /> Watchouts
                        </div>
                        <ul className="mt-4 space-y-2 text-[12.5px] text-lodestar-white-pearl leading-snug">
                            <li>· You over-prepare instead of starting</li>
                            <li>· You stay too long in places that stopped feeding you</li>
                        </ul>
                        <p className="text-[10px] font-sans italic uppercase tracking-[0.14em] text-lodestar-white-mist/45 mt-4">
                            Gentle nudges — not warnings.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// Unlock — 24-Month Life Timeline (premium upsell card)
// ────────────────────────────────────────────────────────────────────────────
function UnlockTimeline() {
    return (
        <section
            data-testid="landing-unlock"
            className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
        >
            <div className="royal-unlock-panel relative grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7 relative z-10">
                    <div className="royal-kicker">
                        <Sparkles className="w-3 h-3" /> Premium
                    </div>
                    <h2 className="royal-display text-3xl lg:text-5xl mt-5 leading-tight">
                        Unlock Your <em>24-Month Life Timeline</em>
                    </h2>
                    <p className="royal-body text-[14.5px] mt-6 max-w-xl leading-[1.7]">
                        Go deeper with a month-by-month dasha-bhukti prediction covering
                        opportunities, challenges, career timing, love, money, emotional
                        patterns, and decision windows. Beautifully printable. Yours to keep.
                    </p>
                </div>
                <div className="lg:col-span-5 relative z-10 flex flex-col gap-3 items-stretch">
                    <Link to="/pricing" className="royal-cta-primary w-full text-center" data-testid="landing-unlock-primary">
                        <Sparkles className="w-3.5 h-3.5" />
                        Download Full Cosmic Report
                    </Link>
                    <Link to="/start" className="royal-cta-secondary w-full justify-center" data-testid="landing-unlock-secondary">
                        Preview Detailed Timeline
                    </Link>
                </div>
            </div>
        </section>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// How it works
// ────────────────────────────────────────────────────────────────────────────
function HowItWorks() {
    const steps = [
        { kicker: "01", title: "Tell us the moment you arrived",
          body: "Date, time, and the city you were born in. Three pieces of information. Two minutes." },
        { kicker: "02", title: "We read the sky for that exact second",
          body: "Your unique chart is mapped — every planet, house, and timing window held to the half-degree." },
        { kicker: "03", title: "Your map arrives",
          body: "Personal. Specific. Dated. Yours to keep, return to, share with the people you trust." },
    ];
    return (
        <section id="how" className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5">
                    <div className="royal-kicker">How it works</div>
                    <h2 className="royal-display text-4xl lg:text-6xl mt-6 leading-[1.06]">
                        Quiet on the outside.<br />
                        <em>Precise underneath.</em>
                    </h2>
                    <p className="royal-body text-[14.5px] mt-7 max-w-md leading-[1.7]">
                        No questionnaires. No quizzes pretending to know you. Just
                        the chart of the moment you were born — and a beautifully
                        guided reading of what it is asking of you next.
                    </p>
                </div>
                <ol className="lg:col-span-7 space-y-5">
                    {steps.map((s) => (
                        <li key={s.kicker} className="flex items-start gap-5 royal-panel" data-testid={`landing-step-${s.kicker}`}>
                            <div className="font-display text-5xl text-lodestar-gold-soft leading-none shrink-0 w-16">{s.kicker}</div>
                            <div>
                                <div className="font-heading text-[22px] text-lodestar-white-pure leading-tight">{s.title}</div>
                                <p className="text-[13.5px] text-lodestar-white-mist mt-2 leading-[1.7]">{s.body}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
}


// ────────────────────────────────────────────────────────────────────────────
// PAGE
// ────────────────────────────────────────────────────────────────────────────
export default function Landing() {
    const { user } = useAuth();
    const [scrollProgress, setScrollProgress] = useState(0);

    // Phase W (perf): defer below-the-fold chunk download until the user
    // starts scrolling past the hero. Use IntersectionObserver if available,
    // else requestIdleCallback, else a small timeout.
    const belowFoldSentinel = useRef(null);
    const [showBelowFold, setShowBelowFold] = useState(false);

    useEffect(() => {
        document.title = "Lodestar — A royal observatory for your life";
        const onScroll = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            setScrollProgress(max > 0 ? window.scrollY / max : 0);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (showBelowFold) return undefined;
        // Strategy 1 — IntersectionObserver on the sentinel placed where the
        // 'How it works' section ends. Fires ~1000px before that section enters
        // the viewport so the chunk is ready by the time the user scrolls there.
        if ("IntersectionObserver" in window && belowFoldSentinel.current) {
            const io = new IntersectionObserver(
                (entries) => {
                    if (entries.some((e) => e.isIntersecting)) {
                        setShowBelowFold(true);
                        io.disconnect();
                    }
                },
                { rootMargin: "1200px 0px" },
            );
            io.observe(belowFoldSentinel.current);
            return () => io.disconnect();
        }
        // Strategy 2 — idle callback (Chromium / desktop Safari supports this).
        if ("requestIdleCallback" in window) {
            const id = window.requestIdleCallback(() => setShowBelowFold(true), { timeout: 2500 });
            return () => window.cancelIdleCallback?.(id);
        }
        // Strategy 3 — small timeout fallback so we never strand the chunk.
        const t = window.setTimeout(() => setShowBelowFold(true), 1800);
        return () => window.clearTimeout(t);
    }, [showBelowFold]);

    return (
        <div
            data-testid="landing-root"
            className="relative min-h-screen text-lodestar-white-pure overflow-hidden"
            style={{ background: "#12051F" }}
        >
            <SEO
                title="Lodestar — Your Personal Cosmic Life Map"
                description="Decode your life patterns, planetary influences, timing, and personal direction. Free cosmic reading in 60 seconds — backed by Swiss Ephemeris + KP astrology."
                path="/"
                noSuffix
            />
            <CosmicBackdrop variant="dramatic" showMountain />

            <div className="relative">
                <NavBar user={user} />
                <Hero />
            </div>

            <div className="relative">
                {/* Proof strip — silent unless ≥25 tracked outcomes exist. */}
                <Suspense fallback={null}>
                    <ProofStrip />
                </Suspense>
                <Hooks />
                <div className="royal-divider-star max-w-screen-md mx-auto">
                    <LodestarMark size={28} showHalo={false} />
                </div>
                <LifeOverviewPreview />
                <UnlockTimeline />
                <HowItWorks />
                <div className="royal-divider max-w-screen-md mx-auto" />
                {/* Sentinel that triggers the below-fold chunk download */}
                <div ref={belowFoldSentinel} aria-hidden="true" data-testid="landing-below-fold-sentinel" />
                {showBelowFold && (
                    <Suspense fallback={null}>
                        <LandingBelowFold />
                    </Suspense>
                )}
                <Footer />
            </div>

            {/* Scroll-progress gold bar */}
            <div
                aria-hidden="true"
                className="royal-scroll-progress"
                style={{
                    transform: `scaleX(${scrollProgress})`,
                    opacity: scrollProgress > 0.02 ? 1 : 0,
                }}
            />
        </div>
    );
}
